// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModalOpen, setModalState, modalViewsConst } from "../../store/modalSlice";
import {
  createComposedForecast,
  updateComposedForecast,
  addDataSource,
  updateDataSource,
  removeDataSource,
  fetchComposedForecasts,
  restampDataSources,
} from "../../store/composedForecastSlice";
import { getMyOrganizations } from "../../store/userSlice";
import ModalBase from "../Modal/ModalBase";
import DataSourceRow from "./DataSourceRow";
import { ReactComponent as PlusIcon } from "../../assets/icons/plus.svg";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import {
  diffDataSources,
  forecastToFormFields,
  stampSourceOrder,
  viewYAxisLabel,
  withYAxisLabel,
  Y_AXIS_LABEL_MAX,
  resolveFormSourceRoles,
  sourceConfigurationError,
  withoutBaseObservation,
} from "../../utils/composedForecastUtils";
import {
  defaultForecastOrganizationId,
  resolveForecastOwnership,
  sourcesNeedingRestamp,
} from "../../utils/composedForecastOwnership";
import type { ComposedForecast, DataSourceFormState } from "../../types/composedForecast";
import type { AppDispatch, RootState } from "../../store";

import Select from "../ReusableComponents/Select";
interface ComposedForecastFormModalProps {
  mode: "create" | "edit";
  forecast?: ComposedForecast;
  onClose?: () => void;
}

const FORECAST_TYPES = ["ensemble", "weighted", "ml-based", "statistical"];
const AGGREGATION_METHODS = ["mean", "weighted_average", "max", "min", "median"];

let nextClientKey = 0;
const createEmptyDataSource = (): DataSourceFormState => ({
  sourceType: "POSTGRES_HISTORICAL",
  sourceName: "",
  weight: 1.0,
  priority: 0,
  // Rows can move, so a new row needs its own key rather than its position
  clientKey: `new-${nextClientKey++}`,
});

const ComposedForecastFormModal: React.FC<ComposedForecastFormModalProps> = ({
  mode,
  forecast,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user);
  const { capture } = usePostHogCapture();
  const organizations = user?.organizations;
  const isAdmin = !!user?.details?.groups?.includes("Admin");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [forecastType, setForecastType] = useState("ensemble");
  const [aggregationMethod, setAggregationMethod] = useState("mean");
  const [isActive, setIsActive] = useState(true);
  const [dataSources, setDataSources] = useState<DataSourceFormState[]>([]);
  // Days of history before the forecast; "" = as long as the forecast (saved on the main forecast source)
  const [historyDays, setHistoryDays] = useState<string>("");
  // What that history ends at: the forecast issue picked in the chart (default) or today
  const [historyAnchor, setHistoryAnchor] = useState<"issue" | "today">("issue");
  // The chart's y-axis title; "" = the data's own unit
  const [yAxisLabel, setYAxisLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Each model-output row's variable in use (its saved choice, else the file's first), keyed by rowKey
  const [resolvedVariations, setResolvedVariations] = useState<Record<string, string | undefined>>({});
  // Org members can read the view only through this; "" = no organization (owner and Admins only)
  const [organizationId, setOrganizationId] = useState(() =>
    mode === "edit" ? forecast?.organizationId || "" : defaultForecastOrganizationId(user?.permissionDetails?.organizationIds)
  );

  // The picker lists user.organizations (every org for an Admin, the user's own otherwise)
  useEffect(() => {
    if (!organizations?.loaded) dispatch(getMyOrganizations());
  }, [dispatch, organizations?.loaded]);

  // An edit starts from the saved view; create keeps the defaults the state was seeded with
  useEffect(() => {
    if (mode !== "edit" || !forecast) return;
    const fields = forecastToFormFields(forecast);
    setName(fields.name);
    setDescription(fields.description);
    setForecastType(fields.forecastType);
    setAggregationMethod(fields.aggregationMethod);
    setIsActive(fields.isActive);
    setDataSources(fields.dataSources);
    const savedHistory = fields.dataSources.find((ds) => ds.historyDays)?.historyDays;
    setHistoryDays(savedHistory ? String(savedHistory) : "");
    setHistoryAnchor(fields.dataSources.some((ds) => ds.historyAnchor === "today") ? "today" : "issue");
    setYAxisLabel(viewYAxisLabel(forecast) || "");
    setOrganizationId(forecast.organizationId || "");
  }, [mode, forecast]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setModalOpen(false));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (dataSources.length === 0) newErrors.dataSources = "At least one data source is required";

    for (let i = 0; i < dataSources.length; i++) {
      if (!dataSources[i].sourceName.trim()) {
        newErrors[`ds_${i}_name`] = "Source name is required";
      }
      // An incomplete row would save and then be dropped from the chart with no message; refuse it here instead
      const missing = sourceConfigurationError(dataSources[i]);
      if (missing) newErrors[`ds_${i}_config`] = missing;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      // Each row saves its place in the list (the draw order) and the main forecast carries the history setting
      const sources = stampSourceOrder(dataSources, parseInt(historyDays, 10) || null, historyAnchor);
      const ownership = resolveForecastOwnership({
        selectedOrganizationId: organizationId,
        organizations: organizations?.data ?? {},
        isAdmin,
        existing: mode === "edit" ? forecast : undefined,
      });

      if (mode === "create") {
        const forecastInput = {
          name: name.trim(),
          description: description.trim() || undefined,
          forecastType,
          aggregationMethod,
          isActive,
          outputParameters: withYAxisLabel(null, yAxisLabel),
          // No owner = AppSync stamps the caller
          ...(ownership.owner ? { owner: ownership.owner } : {}),
          organizationId: ownership.organizationId,
        };

        await dispatch(
          createComposedForecast({
            forecast: forecastInput,
            dataSources: sources,
          })
        ).unwrap();
      } else if (mode === "edit" && forecast) {
        // 1. Update forecast fields
        await dispatch(
          updateComposedForecast({
            id: forecast.id,
            updates: {
              name: name.trim(),
              description: description.trim() || undefined,
              forecastType,
              aggregationMethod,
              isActive,
              // Other keys on outputParameters are kept; only the label changes
              outputParameters: withYAxisLabel(forecast.outputParameters, yAxisLabel),
              organizationId: ownership.organizationId,
              ...(ownership.owner && ownership.owner !== forecast.owner ? { owner: ownership.owner } : {}),
            },
          })
        ).unwrap();

        // 2. Write only what changed: removed sources, edited sources, new sources. Every source carries the
        // forecast's owner and org (never the editor's), or org members lose sight of it.
        const savedSources = forecast.dataSources?.items || [];
        const { toRemove, toUpdate, toCreate } = diffDataSources(savedSources, sources);
        const ownerInfo = {
          owner: ownership.owner || forecast.owner,
          organizationId: ownership.organizationId,
        };
        const restampIds = sourcesNeedingRestamp(
          savedSources,
          new Set(sources.filter((ds) => ds.id).map((ds) => ds.id)),
          new Set(toUpdate.map((ds) => ds.id)),
          ownerInfo
        );

        await Promise.all(
          toRemove.map((id) => dispatch(removeDataSource({ id, composedForecastId: forecast.id })).unwrap())
        );
        await Promise.all(
          toUpdate.map((ds) =>
            dispatch(updateDataSource({ composedForecastId: forecast.id, dataSource: ds, ...ownerInfo })).unwrap()
          )
        );
        await Promise.all(
          toCreate.map((ds) =>
            dispatch(addDataSource({ composedForecastId: forecast.id, dataSource: ds, ...ownerInfo })).unwrap()
          )
        );
        if (restampIds.length) {
          await dispatch(restampDataSources({ ids: restampIds, ...ownerInfo })).unwrap();
        }
      }

      capture(
        mode === "create"
          ? POSTHOG_EVENTS.COMPOSED_FORECAST_CREATED
          : POSTHOG_EVENTS.COMPOSED_FORECAST_UPDATED,
        {
          forecast_name: name.trim(),
          forecast_type: forecastType,
          aggregation_method: aggregationMethod,
          data_source_count: dataSources.length,
          source_types: dataSources.map((ds) => ds.sourceType),
        }
      );

      // Refresh the list
      dispatch(fetchComposedForecasts());
      handleClose();
    } catch (error) {
      console.error("Error saving composed forecast:", error);
      setErrors({ submit: "Failed to save. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDataSource = () => {
    setDataSources((prev) => [...prev, createEmptyDataSource()]);
  };

  const handleUpdateDataSource = (index: number, updated: DataSourceFormState) => {
    setDataSources((prev) => {
      // A forecast is scored against one observation: marking this source clears the others
      const claimsObservation = updated.accuracy?.enabled && !prev[index]?.accuracy?.enabled;
      const next = prev.map((ds, i) =>
        claimsObservation && i !== index && ds.accuracy?.enabled ? { ...ds, accuracy: null } : ds
      );
      next[index] = updated;
      // A flag that can't apply (e.g. on the only model output) is dropped (#908)
      return withoutBaseObservation(next);
    });
  };

  const handleMoveDataSource = (index: number, delta: -1 | 1) => {
    setDataSources((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleRemoveDataSource = (index: number) => {
    // Removing a model output can leave the observation as the only one — drop its flag then (#908)
    setDataSources((prev) => withoutBaseObservation(prev.filter((_, i) => i !== index)));
  };

  // Swaps this modal for the confirm rather than stacking one over the other
  const handleDelete = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.deleteComposedForecast,
        properties: {
          __modalSize: "sm",
          id: forecast.id,
          name: forecast.name,
          dataSources: forecast.dataSources?.items || [],
        },
      })
    );
  };

  // A row's identity across edits and moves: its id once saved, else the key it was created with
  const rowKey = (ds: DataSourceFormState, index: number) => ds.id ?? ds.clientKey ?? `new-${index}`;

  // Which row is the base model — resolved once per render for every DataSourceRow
  const formRoles = resolveFormSourceRoles(dataSources);
  const baseSource = dataSources[formRoles.baseIndex];
  // What the base model really draws, so the observation offers that variable's traces and bands
  const baseVariation = baseSource
    ? baseSource.variation || resolvedVariations[rowKey(baseSource, formRoles.baseIndex)]
    : undefined;

  const formButtons = (
    <>
      <button type="button" onClick={handleClose} className="eai-btn-outline" disabled={submitting}>
        Cancel
      </button>
      <button type="button" onClick={handleSubmit} className="eai-btn-cta" disabled={submitting}>
        {submitting ? "Saving..." : mode === "create" ? "Create" : "Save Changes"}
      </button>
    </>
  );

  const footer =
    mode === "edit" && forecast ? (
      // One child so full-screen's [&>*]:flex-1 can't squeeze three pills; on a phone Delete takes its own row below Cancel/Save
      <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:justify-end">
        <button
          type="button"
          onClick={handleDelete}
          className="eai-btn-danger order-last col-span-2 sm:order-none"
          disabled={submitting}
        >
          Delete Custom View
        </button>
        {formButtons}
      </div>
    ) : (
      formButtons
    );

  return (
    <ModalBase
      title={mode === "create" ? "Create Custom View" : "Edit Custom View"}
      onClose={handleClose}
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        {errors.submit && (
          <div
            role="alert"
            className="rounded-eai-card border border-eai-danger-border bg-eai-danger-surface px-3 py-2 font-inter text-eai-body-sm text-eai-danger-text"
          >
            {errors.submit}
          </div>
        )}

        {/* Basic Info */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="eai-meta-label mb-2 block">
              Name <span className="text-eai-danger-text">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
              }}
              className={`eai-input ${errors.name ? "border-eai-danger-border-strong" : ""}`}
              placeholder="e.g., Wind Farm Power Forecast"
            />
            {errors.name && (
              <p className="mt-1 font-inter text-eai-body-xs text-eai-danger-text">{errors.name}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="eai-meta-label mb-2 block">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="eai-input resize-none"
              placeholder="Describe what this forecast produces..."
            />
          </div>

          <div>
            <Select
              label="Forecast Type"
              value={forecastType}
              onChange={(e) => setForecastType(e.target.value)}
              containerClassName="w-full"
            >
              {FORECAST_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1).replace("-", " ")}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Select
              label="Aggregation Method"
              value={aggregationMethod}
              onChange={(e) => setAggregationMethod(e.target.value)}
              containerClassName="w-full"
            >
              {AGGREGATION_METHODS.map((method) => (
                <option key={method} value={method}>
                  {method.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Select
              label="Organization"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              containerClassName="w-full"
              hint={
                organizationId
                  ? "Everyone in this organization can see this view and its data sources."
                  : "Only you and Admins can see this view."
              }
            >
              <option value="">No Organization</option>
              {/* The saved org stays listed even before the org list loads */}
              {organizationId && !organizations?.data?.[organizationId] && (
                <option value={organizationId}>{organizationId}</option>
              )}
              {Object.entries(organizations?.data ?? {})
                .sort(([, a], [, b]) => (a.name || "").localeCompare(b.name || ""))
                .map(([id, org]) => (
                  <option key={id} value={id}>
                    {org.name || id}
                  </option>
                ))}
            </Select>
            {isAdmin &&
              organizationId &&
              organizations?.data?.[organizationId]?.owner &&
              (mode === "create" || organizationId !== (forecast?.organizationId || "")) && (
              <p className="mt-1 font-inter text-eai-body-xs text-eai-warning-text">
                Saving hands this view to the organization's owner.
              </p>
            )}
          </div>
        </div>

        {/* Same switch as the model cards, so on/off reads the same across the app */}
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            role="switch"
            id="isActive"
            aria-checked={isActive}
            onClick={() => setIsActive(!isActive)}
            className={`relative inline-flex h-4 w-8 items-center rounded-full p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eai-focus-neutral focus-visible:ring-offset-1 ${
              isActive ? "bg-eai-cta-primary" : "bg-eai-border-hover"
            }`}
          >
            <span
              className={`inline-block h-3 w-3 rounded-full bg-eai-surface-card shadow-eai-subtle transition-transform ${
                isActive ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
          <label htmlFor="isActive" className="cursor-pointer font-inter text-eai-body-sm text-eai-ink-muted">
            {isActive ? "Active" : "Inactive"}
          </label>
        </div>

        {/* The chart's y-axis title, for when the data's own unit reads badly (degree_Celsius → °C) */}
        <div>
          <label htmlFor="yAxisLabel" className="eai-meta-label mb-2 block">Y-axis label</label>
          <input
            id="yAxisLabel"
            type="text"
            value={yAxisLabel}
            maxLength={Y_AXIS_LABEL_MAX}
            onChange={(e) => setYAxisLabel(e.target.value)}
            placeholder="e.g. °C, ft³/s, MW"
            className="eai-input sm:w-64"
          />
          <p className="mt-1 font-inter text-eai-body-xs text-eai-ink-faint">
            Leave blank to show the unit from the data.
          </p>
        </div>

        {/* History before the forecast: only means something when the view has a forecast */}
        {baseSource && (
          <div>
            <label htmlFor="historyDays" className="eai-meta-label mb-2 block">History before the forecast</label>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <input
                id="historyDays"
                type="number"
                min="1"
                max="730"
                value={historyDays}
                onChange={(e) => setHistoryDays(e.target.value)}
                placeholder="Auto"
                className="eai-input w-24 shrink-0 sm:w-32"
              />
              <span className="font-inter text-eai-body-xs text-eai-ink-faint">days, ending at</span>
              <Select
                aria-label="History ends at"
                value={historyAnchor}
                onChange={(e) => setHistoryAnchor(e.target.value as "issue" | "today")}
                size="sm"
              >
                <option value="issue">the selected forecast issue</option>
                <option value="today">today</option>
              </Select>
            </div>
            <p className="mt-1 font-inter text-eai-body-xs text-eai-ink-faint">
              {historyAnchor === "issue"
                ? "Stepping back to an older forecast issue brings the history before that issue. "
                : "The history stays the days before today, whichever forecast issue is picked, so an older issue can sit apart from it with a gap between. "}
Applies to every source in the view. Leave the days blank to show as much history as the forecast is long.
            </p>
          </div>
        )}

        {/* Data Sources */}
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            {/* Labelled like every other field above it */}
            <p className="eai-meta-label">
              Data Sources
              {errors.dataSources && (
                <span className="ml-2 font-inter text-eai-body-xs font-normal normal-case text-eai-danger-text">{errors.dataSources}</span>
              )}
            </p>
            <button type="button" onClick={handleAddDataSource} className="eai-btn-outline-xs">
              {/* plus.svg hardcodes stroke="white", so on an outline pill it needs currentColor */}
              <PlusIcon className="h-3 w-3 [&_path]:stroke-current" />
              Add Data Source
            </button>
          </div>

          {dataSources.length === 0 ? (
            // The header's Add Data Source is the one way in, so the empty state only explains
            <div className="rounded-eai-card border border-dashed border-eai-border py-8 text-center">
              <p className="eai-description-sm">No data sources yet — add one to start the view.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dataSources.map((ds, index) => (
                <DataSourceRow
                  key={rowKey(ds, index)}
                  dataSource={ds}
                  error={errors[`ds_${index}_name`]}
                  configError={errors[`ds_${index}_config`]}
                  isBaseModel={index === formRoles.baseIndex}
                  baseOutputId={baseSource?.processedModelOutputId}
                  baseVariation={baseVariation}
                  onResolvedVariation={(variation) =>
                    setResolvedVariations((prev) =>
                      prev[rowKey(ds, index)] === variation ? prev : { ...prev, [rowKey(ds, index)]: variation }
                    )
                  }
                  onUpdate={(updated) => handleUpdateDataSource(index, updated)}
                  onRemove={() => handleRemoveDataSource(index)}
                  onMoveUp={index > 0 ? () => handleMoveDataSource(index, -1) : undefined}
                  onMoveDown={index < dataSources.length - 1 ? () => handleMoveDataSource(index, 1) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  );
};

export default ComposedForecastFormModal;
